import { useEffect, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, Link as MuiLink, Typography } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import { getClimateHazardMethodology } from '../climateAnalytics/common/hazardConfig'

export type MethodologyContentVariant = 'drawer' | 'page'

type MethodologyContentProps = {
  hazardCode: string
  /** Optional section id (matching a slug produced by rehype-slug) to scroll to once loaded. */
  anchor?: string
  variant?: MethodologyContentVariant
}

/** In-memory cache of fetched markdown payloads, keyed by absolute doc URL. */
const markdownCache = new Map<string, string>()

async function loadMarkdown(url: string): Promise<string> {
  const cached = markdownCache.get(url)
  if (cached !== undefined) return cached
  const response = await fetch(url, { headers: { Accept: 'text/markdown,text/plain' } })
  if (!response.ok) {
    throw new Error(`Failed to load methodology (${response.status})`)
  }
  const text = await response.text()
  markdownCache.set(url, text)
  return text
}

/**
 * Renders a hazard's methodology markdown document.
 *
 * The markdown source is fetched from `/methodology/<docPath>` (populated by
 * `scripts/copy-methodology.js` at build/start time). Headings receive
 * auto-generated slug ids via `rehype-slug`; relative image references in the
 * markdown are resolved against the same directory as the markdown file so the
 * embedded diagrams continue to work after the copy.
 */
export default function MethodologyContent({ hazardCode, anchor, variant = 'page' }: MethodologyContentProps) {
  const methodology = getClimateHazardMethodology(hazardCode)
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const docUrl = methodology ? `${process.env.PUBLIC_URL ?? ''}/methodology/${methodology.docPath}` : null
  const baseHref = docUrl ? docUrl.slice(0, docUrl.lastIndexOf('/') + 1) : ''

  useEffect(() => {
    let cancelled = false
    if (!docUrl) {
      setContent(null)
      setError('Methodology not available for this hazard.')
      return () => {
        cancelled = true
      }
    }
    setError(null)
    setContent(null)
    loadMarkdown(docUrl)
      .then(text => {
        if (!cancelled) setContent(text)
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load methodology')
      })
    return () => {
      cancelled = true
    }
  }, [docUrl])

  // Scroll to anchor after content renders. Lookup is scoped to the container
  // so duplicate ids elsewhere on the page never hijack the scroll target.
  useEffect(() => {
    if (!content || !anchor || !containerRef.current) return
    const target = containerRef.current.querySelector<HTMLElement>(`#${CSS.escape(anchor)}`)
    if (target) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' })
    }
  }, [content, anchor])

  if (!methodology) {
    return (
      <Box p={2}>
        <Alert severity="info">No methodology documentation is published for this hazard yet.</Alert>
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    )
  }

  if (content == null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const headingScale = variant === 'drawer' ? 0.9 : 1

  return (
    <Box
      ref={containerRef}
      sx={{
        // Tight, readable typography that adapts to drawer vs. page.
        color: 'text.primary',
        wordBreak: 'break-word',
        '& h1': {
          fontSize: `${1.75 * headingScale}rem`,
          fontWeight: 700,
          mt: 0,
          mb: 2,
          scrollMarginTop: 72,
        },
        '& h2': {
          fontSize: `${1.35 * headingScale}rem`,
          fontWeight: 700,
          mt: 4,
          mb: 1.5,
          pb: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          scrollMarginTop: 72,
        },
        '& h3': {
          fontSize: `${1.1 * headingScale}rem`,
          fontWeight: 600,
          mt: 3,
          mb: 1,
          scrollMarginTop: 72,
        },
        '& p': { lineHeight: 1.65, my: 1.25 },
        '& ul, & ol': { pl: 3, my: 1 },
        '& li': { my: 0.5 },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          my: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          my: 2,
          fontSize: '0.875rem',
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          px: 1.25,
          py: 0.75,
          textAlign: 'left',
        },
        '& th': { backgroundColor: 'action.hover', fontWeight: 700 },
        '& code': {
          fontFamily: 'monospace',
          backgroundColor: 'action.hover',
          px: 0.5,
          borderRadius: 0.5,
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          pl: 2,
          my: 2,
        },
      }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{
          a: ({ node, href, children, ...rest }) => (
            <MuiLink
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              {...rest}>
              {children}
            </MuiLink>
          ),
          img: ({ node, src, alt, ...rest }) => {
            // Resolve relative image paths against the markdown file's directory.
            const resolved = src && !/^(https?:|data:|\/)/.test(src) ? `${baseHref}${src}` : src
            return <img src={resolved} alt={alt ?? ''} loading="lazy" {...rest} />
          },
          p: ({ node, children, ...rest }) => (
            <Typography component="p" variant="body1" {...rest}>
              {children}
            </Typography>
          ),
        }}>
        {content}
      </ReactMarkdown>
    </Box>
  )
}
